<template>
  <transition
    name="editor-drawer-transition"
    enter-from-class="editor-drawer-transition--enter-from"
    enter-active-class="editor-drawer-transition--enter-active"
    leave-active-class="editor-drawer-transition--leave-active"
    leave-to-class="editor-drawer-transition--leave-to"
  >
    <div
      v-show="drawerOpen"
      class="editor-drawer editor-drawer--right chat-drawer"
      :class="drawerOpen && 'chat-drawer--open'"
    >
      <div
        :aria-hidden="(!drawerOpen).toString()"
        class="editor-drawer__scroll-wrap chat-drawer__scroll-wrap"
      >
        <div class="editor-drawer__heading-wrapper chat-drawer__heading-wrapper">
          <h2 class="editor-drawer__heading chat-drawer__heading">
            {{ $t('Chat') }}
          </h2>
          <label
            v-if="userHasAdminPermissions"
            class="chat-drawer__enable-toggle-wrapper"
          >
            <input
              :aria-label="$t('Enable Chat')"
              class="nebula-screenreader-only chat-drawer__enable-toggle-input"
              type="checkbox"
              :checked="chatToggle.enabled"
              @change="toggleEnableChat"
            >
            <div
              aria-hidden="true"
              class="chat-drawer__enable-toggle"
              :class="chatToggle.enabled || 'chat-drawer__enable-toggle--disabled'"
            >
              <span
                v-if="chatToggle.enabled"
                class="chat-drawer__enable-toggle-text"
              >
                {{ $t('On') }}
              </span>
              <div class="chat-drawer__enable-toggle-dot" />
              <span
                v-if="!chatToggle.enabled"
                class="chat-drawer__enable-toggle-text"
              >
                {{ $t('Off') }}
              </span>
            </div>
          </label>
          <NebulaButton
            ref="closeButton"
            class="editor-drawer__toggle-button editor-drawer__toggle-button--chat"
            aria-expanded="true"
            :aria-label="$t('Close')"
            :disabled="!drawerOpen"
            icon-left="x"
            size="s"
            type="flat"
            @click="toggleDrawer"
          />
        </div>
        <LoadingIndicator
          v-if="chatToggle.enabled && !chatIsConnected"
          class="chat-drawer__loading"
          transparent
        />
        <div
          v-else-if="filteredChatMessages.length && chatToggle.enabled"
          ref="messagesWrapper"
          class="chat-drawer__messages-wrapper"
        >
          <div
            v-for="date in groupedChatMessages"
            :key="date[0].formattedDate"
          >
            <h3 class="chat-drawer__date-heading">
              {{ date[0].formattedDate }}
            </h3>
            <div
              v-for="messageGroup in date"
              :key="messageGroup.messages[0].message_key"
              class="chat-message"
              :class="messageGroup.from_user === user.id
                ? 'chat-message--sent' : 'chat-message--received'"
            >
              <!-- A chat message sent by the current user -->
              <template v-if="messageGroup.from_user === user.id">
                <div class="chat-message__bubble-wrapper">
                  <span class="chat-message__bubble-meta">
                    {{ fullNameFromUserId(messageGroup.from_user) }}
                  </span>
                  <div class="chat-message__bubble">
                    <div
                      v-for="message in messageGroup.messages"
                      :key="message.message_key"
                      class="chat-message__bubble-item"
                    >
                      <div class="chat-message__bubble-text-wrapper">
                        <p
                          class="chat-message__bubble-text"
                          :class="message.queued && 'chat-message__bubble-text--queued'"
                        >
                          {{ message.message }}
                        </p>
                        <div
                          v-if="message.settings && message.settings.is_hidden"
                          class="chat-message__hidden-message"
                        >
                          <NebulaIcon
                            class="chat-message__hidden-message-icon"
                            size="s"
                            symbol-id="message-error"
                          />
                          {{ $t('Message is hidden to non-admins') }}
                        </div>
                      </div>
                      <ChatDrawerMessageOptions
                        v-if="userHasAdminPermissions"
                        :disabled="message.queued"
                        :id="message.message_id"
                        :is-hidden="message.settings && message.settings.is_hidden"
                      />
                    </div>
                  </div>
                  <span class="chat-message__bubble-meta">
                    {{ messageGroup.formattedTime }}
                  </span>
                </div>
                <div class="chat-message__avatar">
                  {{ initialsFromUserId(messageGroup.from_user) }}
                </div>
              </template>

              <!-- A chat message received from another user -->
              <template v-else>
                <div class="chat-message__avatar">
                  {{ initialsFromUserId(messageGroup.from_user) }}
                </div>
                <div class="chat-message__bubble-wrapper">
                  <span class="chat-message__bubble-meta">
                    {{ fullNameFromUserId(messageGroup.from_user) }}
                  </span>
                  <div class="chat-message__bubble">
                    <div
                      v-for="message in messageGroup.messages"
                      :key="message.message_key"
                      class="chat-message__bubble-item"
                    >
                      <div class="chat-message__bubble-text-wrapper">
                        <p class="chat-message__bubble-text">
                          {{ message.message }}
                        </p>
                        <div
                          v-if="message.settings && message.settings.is_hidden"
                          class="chat-message__hidden-message"
                        >
                          <NebulaIcon
                            class="chat-message__hidden-message-icon"
                            size="s"
                            symbol-id="message-error"
                          />
                          {{ $t('Message is hidden to non-admins') }}
                        </div>
                      </div>
                      <ChatDrawerMessageOptions
                        v-if="userHasAdminPermissions"
                        :id="message.message_id"
                        :is-hidden="message.settings && message.settings.is_hidden"
                      />
                    </div>
                  </div>
                  <span class="chat-message__bubble-meta">
                    {{ messageGroup.formattedTime }}
                  </span>
                </div>
              </template>
            </div>
          </div>
        </div>
        <div
          v-if="!chatToggle.enabled || (chatIsConnected && !filteredChatMessages.length)"
          class="chat-drawer__status-message-wrapper"
        >
          <p
            v-if="!chatToggle.enabled"
            class="chat-drawer__status-message chat-drawer__status-message--turned-off"
          >
            {{ $t('Chat is turned off') }}
          </p>
          <p
            v-else-if="!filteredChatMessages.length"
            class="chat-drawer__status-message"
          >
            {{ $t('Your chat log is empty.') }}
          </p>
        </div>
        <form
          v-if="chatToggle.enabled && chatIsConnected"
          class="chat-drawer__input-wrapper"
          @submit.prevent="sendMessage"
        >
          <NebulaTextarea
            ref="messageInput"
            class="chat-drawer__textarea"
            autocapitalize="off"
            autocorrect="off"
            :placeholder="$t('Send a message...')"
            rows="1"
            :text="messageContent"
            @input="messageContent = $event"
            @keydown.enter="checkShiftAndSubmit"
          />
          <div>
            <NebulaButton
              :aria-label="$t('Send')"
              :disabled="!messageContent"
              icon-left="send"
              is-submit
              size="l"
              type="flat"
            />
          </div>
        </form>
      </div>
    </div>
  </transition>
</template>

<script>
import { NebulaButton, NebulaIcon, NebulaTextarea } from '@discoveryedu/nebula-components';
import { cloneDeep, get } from 'lodash-es';
import { mapState, mapActions } from 'pinia';
import swearjar from 'swearjar';
import * as types from '@/lib/constants/store';
import ChatDrawerMessageOptions from '@/components/ChatDrawerMessageOptions.vue';
import LoadingIndicator from '@/components/LoadingIndicator.vue';
import {
  useChatStore,
  useEditorStore,
  useUserStore,
} from '@/stores';

export default {
  name: 'ChatDrawer',
  components: {
    ChatDrawerMessageOptions,
    LoadingIndicator,
    NebulaButton,
    NebulaIcon,
    NebulaTextarea,
  },
  emits: ['focus-open'],
  data() {
    return {
      messageContent: '',
    };
  },
  computed: {
    ...mapState(useUserStore, [
      'user',
      'userIsStudent',
    ]),
    ...mapState(useChatStore, [
      'chatMessages',
      'chatToggle',
      'chatIsConnected',
      'userById',
    ]),
    ...mapState(useEditorStore, [
      'currentModalCanvasId',
      'draft',
      'draftAccessLevel',
      'editorDrawers',
    ]),
    filteredChatMessages() {
      let messages = this.chatMessages;

      if (!this.userHasAdminPermissions) {
        // Some messages may be hidden from non-admins
        messages = this.chatMessages.filter((messageGroup) => (
          !get(messageGroup, 'messages[0].settings.is_hidden')
        ));
      }

      return messages;
    },
    drawerOpen() {
      return this.editorDrawers.chat && !this.currentModalCanvasId;
    },
    groupedChatMessages() {
      // Chat messages are grouped by date and then grouped by time
      return this.filteredChatMessages.reduce((acc, currMessage) => {
        // Prevent accidentally updating the messages in the getter
        const curr = cloneDeep(currMessage);

        const lastDate = acc.length && acc[acc.length - 1];

        if (!acc.length) {
          // The first message can be pushed in on its own
          acc.push([curr]);
        } else if (!curr.formattedDate) {
          // Skip processing missing dates just in case
          lastDate.push(curr);
        } else {
          const lastMessage = lastDate[lastDate.length - 1];

          if (lastMessage.formattedDate === curr.formattedDate) {
            // If the current message is the same date as the previous message,
            // check to see if the times match (times are only evaluated to the minute)
            // and if it was sent by the same user
            if (lastMessage.formattedTime === curr.formattedTime
              && lastMessage.from_user === curr.from_user) {
              lastMessage.messages.push(...curr.messages);
            } else {
              // If they don't match, push the message into the same date array, not
              // grouped by time
              lastDate.push(curr);
            }
          } else {
            // Otherwise make a new subarray with the new message
            acc.push([curr]);
          }
        }

        return acc;
      }, []);
    },
    userHasAdminPermissions() {
      return ['owner', 'admin'].indexOf(this.draftAccessLevel) > -1
        && !this.userIsStudent;
    },
  },
  watch: {
    drawerOpen(isOpen) {
      if (isOpen) {
        // Since this button is offscreen while the drawer is transitioning in, it can mess up
        // the overflow in Chrome. Wait until the transition completes before focusing
        setTimeout(() => {
          this.$refs.closeButton.$el.focus();
        }, 250);
      }
    },
    chatIsConnected(isConnected) {
      // chat connected in the chat store but hasn't been enabled in studio?
      if (
        isConnected
        && !this.draft.options.chat_enabled
        && this.chatToggle.enabled
      ) {
        // update draft to show that chat was enabled. This is done after the connection is made
        // so that the teacher can complete the chat session creation before anyone else connects
        // to it
        this[types.UPDATE_CHAT_ENABLED](true);
      }
    },
    chatMessages(newMessages, oldMessages) {
      // If new messages have been loaded, scroll to the bottom. Note that once messages
      // are paginated and _older_ messages might have been loaded, this will need to change
      if (newMessages.length !== oldMessages.length) {
        this.scrollToBottom();
      }
    },
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.TOGGLE_EDITOR_DRAWER,
      types.UPDATE_CHAT_ENABLED,
    ]),
    ...mapActions(useChatStore, [
      types.SEND_CHAT_MESSAGE,
      types.GET_CHAT,
      types.SET_CHAT_TOGGLE_ENABLED,
    ]),
    checkShiftAndSubmit(e) {
      // Match the behavior in Studio Beta: Shift+Enter adds a line break, but
      // Enter by itself sends the message
      if (!e.shiftKey) {
        e.preventDefault();
        this.sendMessage();
      }
    },
    fullNameFromUserId(userId) {
      const user = this.userById(userId);
      if (user) {
        return `${user.firstName} ${user.lastName}`;
      }
      return '\xa0';
    },
    initialsFromUserId(userId) {
      const user = this.userById(userId);
      if (user) {
        return `${user.firstName[0]}${user.lastName[0]}`;
      }
      return '\xa0';
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (!this.filteredChatMessages.length || !this.$refs.messagesWrapper) return;
        this.$refs.messagesWrapper.scrollTop = this.$refs.messagesWrapper.scrollHeight;
      });
    },
    async sendMessage() {
      // Strip profanity and trailing returns before sending
      const message = swearjar.censor(this.messageContent.replace(/[\r\n]+$/, ''));

      this.messageContent = '';
      await this[types.SEND_CHAT_MESSAGE]({ message });
      this.scrollToBottom();
      this.$nextTick(() => {
        // Restore the focus to the textarea so they can keep sending more messages
        if (this.$refs.messageInput) this.$refs.messageInput.$el.focus();
      });
    },
    toggleDrawer() {
      this[types.TOGGLE_EDITOR_DRAWER]({
        drawer: 'chat',
      });
      this.$emit('focus-open', 'chat');
    },
    toggleEnableChat({ target }) {
      // toggle enabled in the chat drawer
      this[types.SET_CHAT_TOGGLE_ENABLED](target.checked);

      if (!target.checked) {
        // If disabled, disabled in studio draft which will disable for everyone.
        this[types.UPDATE_CHAT_ENABLED](target.checked);
      } else {
        // otherwise, start initializing the chat session
        // after initialization, we'll update the enabled value in studio
        this[types.GET_CHAT]({
          draft: this.draft,
        });
      }
    },
  },
};
</script>

<style lang="stylus">
$chat-bubble-radius = 8px;

.chat-drawer {
  &__scroll-wrap.editor-drawer__scroll-wrap {
    height: 100%;
    position: relative;
  }

  &__heading-wrapper {
    justify-content: space-between;
  }

  &__heading.editor-drawer__heading {
    flex-grow: 0;
  }

  &__enable-toggle-wrapper {
    align-self: center;
    margin-inline-end: auto;
  }

  &__enable-toggle-dot {
    background-color: $nebula-color-white;
    border-radius: 50%;
    height: $nebula-space-2x;
    width: $nebula-space-2x;
  }

  &__enable-toggle-text {
    color: $nebula-color-white;
    font-size: $comet-font-size-metadata;
    line-height: 1;
    padding-block: 0;
    padding-inline: 0 $nebula-space-half;
  }

  &__enable-toggle {
    align-items: center;
    background-color: $nebula-color-platform-interactive-800;
    border-radius: $nebula-space-4x;
    display: flex;
    padding: $nebula-space-half;

    .chat-drawer__enable-toggle-input:focus + & {
      outline: -webkit-focus-ring-color auto 5px;
    }

    &--disabled {
      background-color: $nebula-color-platform-interface-500;

      .chat-drawer__enable-toggle-text {
        padding-block: 0;
        padding-inline: $nebula-space-half 0;
      }
    }
  }

  &__loading {
    flex-grow: 1;
    position: relative;
  }

  &__status-message-wrapper {
    align-items: center;
    flex-grow: 1;
    display: flex;
  }

  &__status-message {
    font-weight: bold;
    margin: 0;
    text-align: center;
    width: 100%;
  }

  &__messages-wrapper {
    flex-grow: 1;
    overflow: auto;
    padding: 0 $nebula-space-2x 0;
  }

  &__date-heading {
    border: $comet-border-hairline-default;
    border-radius: $nebula-space-2x;
    color: $comet-color-neutral-40;
    font-size: $comet-font-size-metadata;
    font-weight: normal;
    line-height: 1;
    margin: 0 0 $nebula-space-2x 0;
    padding: $nebula-space-1x $nebula-space-2x $nebula-space-1x $nebula-space-2x;
    text-align: center;
  }

  &__input-wrapper {
    border-top: $comet-border-hairline-default;
    display: flex;

    .chat-drawer__textarea.nebula-textarea {
      border: none;
      border-radius: 0;
      flex-grow: 1;
      margin: 0;
      min-height: $nebula-space-1x;
      padding: $nebula-space-1x $nebula-space-2x $nebula-space-1x $nebula-space-2x;
      resize: none;
      width: 100%;
    }

    .nebula-button {
      border-radius: 0;
      margin: 0;

      &:disabled {
        background-color: transparent;

        svg {
          fill: rgba($nebula-color-platform-interactive-800, .3);
        }
      }
    }
  }
}

.chat-message {
  display: flex;
  margin: 0 0 $nebula-space-2x 0;

  &__avatar {
    background-color: $comet-color-neutral-90;
    border-radius: 50%;
    flex-shrink: 0;
    height: $nebula-space-4x;
    line-height: $nebula-space-4x;
    margin-block: $nebula-space-2x 0;
    margin-inline: 0 $nebula-space-1x;
    text-align: center;
    width: $nebula-space-4x;

    .chat-message--sent & {
      background-color: $comet-color-yellow-70;
      margin-block: $nebula-space-2x 0;
      margin-inline: $nebula-space-1x 0;
    }
  }

  &__bubble-wrapper {
    flex-grow: 1;
    flex-shrink: 1;
    min-width: 0;
  }

  &__bubble-meta {
    color: $comet-color-neutral-40;
    display: block;
    font-size: $comet-font-size-metadata;
    line-height: 1;

    .chat-message--sent & {
      text-align: end;
    }
  }

  &__bubble {
    background-color: $comet-color-neutral-90;
    border-radius: $chat-bubble-radius;
    margin: $nebula-space-half 0;
    padding-block: $nebula-space-1x;
    padding-inline: $nebula-space-2x 0;

    .chat-message--sent & {
      background-color: $comet-color-yellow-70;
    }
  }

  &__bubble-item {
    display: flex;
    justify-content: space-between;
  }

  &__bubble-text-wrapper {
    flex-shrink: 1;
    min-width: 0;
    overflow-wrap: break-word;
  }

  &__bubble-text {
    margin: 0;
    padding-inline-end: $nebula-space-2x;
    white-space: pre-line;

    &--queued {
      opacity: 0.6;
    }
  }

  &__hidden-message {
    nebula-text-italic();
    display: flex;
    font-size: $comet-font-size-metadata;
  }

  &__hidden-message-icon {
    fill: $comet-color-error-default;
    margin-inline-end: $nebula-space-1x;
  }
}
</style>
